<script setup>
import { onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'

const props = defineProps({
  active: Boolean,
  softwareRotated: Boolean,
  dual: Boolean
})

const emit = defineEmits(['change', 'interact'])

const leftPad = ref(null)
const rightPad = ref(null)
const sticks = reactive({
  left: createStickState(),
  right: createStickState()
})
const touchSides = new Map()

function createStickState() {
  return {
    pointerId: null,
    inputType: null,
    x: 0,
    y: 0,
    thumbX: 0,
    thumbY: 0
  }
}

function padElement(side) {
  return side === 'left' ? leftPad.value : rightPad.value
}

function eventPoint(point) {
  if (!props.softwareRotated) {
    return { x: point.clientX, y: point.clientY }
  }

  // The software-landscape fallback rotates the whole viewer 90 degrees.
  // Pointer client coordinates stay in the unrotated viewport, so map them
  // back into the logical landscape coordinate system used by the controls.
  const logicalHeight = document.documentElement.clientWidth || window.innerWidth
  return {
    x: point.clientY,
    y: logicalHeight - point.clientX
  }
}

function padMetrics(side) {
  const element = padElement(side)
  if (!element) return null

  if (props.softwareRotated) {
    const radius = element.offsetWidth / 2
    return {
      centerX: element.offsetLeft + radius,
      centerY: element.offsetTop + element.offsetHeight / 2,
      radius
    }
  }

  const rect = element.getBoundingClientRect()
  return {
    centerX: rect.left + rect.width / 2,
    centerY: rect.top + rect.height / 2,
    radius: rect.width / 2
  }
}

function emitValues() {
  emit('change', {
    left: [sticks.left.x, sticks.left.y],
    right: props.dual ? [sticks.right.x, sticks.right.y] : [0, 0]
  })
}

function updateStick(side, point, emitChange = true) {
  const state = sticks[side]
  const metrics = padMetrics(side)
  if (!metrics) return

  const position = eventPoint(point)
  const thumbRadius = Math.max(12, metrics.radius / 3)
  const maxReach = Math.max(1, Math.min(50, metrics.radius - thumbRadius))
  let dx = position.x - metrics.centerX
  let dy = position.y - metrics.centerY
  const distance = Math.hypot(dx, dy)
  if (distance > maxReach) {
    const scale = maxReach / distance
    dx *= scale
    dy *= scale
  }

  const deadZone = 0.1
  const normalizedDistance = Math.hypot(dx, dy) / maxReach
  const axisScale = normalizedDistance < deadZone ? 0 : side === 'right'
    ? (normalizedDistance - deadZone) / ((1 - deadZone) * normalizedDistance)
    : 1
  state.x = dx / maxReach * axisScale
  state.y = -dy / maxReach * axisScale
  state.thumbX = dx
  state.thumbY = dy
  if (emitChange) emitValues()
}

function start(side, event) {
  if (event.pointerType === 'touch') return
  if (!props.active || sticks[side].pointerId !== null) return
  event.preventDefault()
  event.stopPropagation()
  sticks[side].pointerId = event.pointerId
  sticks[side].inputType = 'pointer'
  try {
    event.currentTarget.setPointerCapture?.(event.pointerId)
  } catch {
    // Older embedded WebViews can support Pointer Events without capture.
  }
  updateStick(side, event)
  emit('interact')
}

function move(side, event) {
  if (event.pointerType === 'touch' || sticks[side].inputType !== 'pointer' || sticks[side].pointerId !== event.pointerId) return
  event.preventDefault()
  event.stopPropagation()
  updateStick(side, event)
}

function reset(side, emitChange = true) {
  const state = sticks[side]
  state.pointerId = null
  state.inputType = null
  state.x = 0
  state.y = 0
  state.thumbX = 0
  state.thumbY = 0
  if (emitChange) emitValues()
}

function finish(side, event) {
  if (event.pointerType === 'touch' || sticks[side].inputType !== 'pointer' || sticks[side].pointerId !== event.pointerId) return
  event.preventDefault()
  event.stopPropagation()
  const target = event.currentTarget
  reset(side)
  try {
    target.releasePointerCapture?.(event.pointerId)
  } catch {
    // The browser may already have released capture during orientation change.
  }
}

function sideForTouch(touch) {
  const sides = props.dual ? ['left', 'right'] : ['left']
  for (const side of sides) {
    const rect = padElement(side)?.getBoundingClientRect?.()
    if (
      rect && touch.clientX >= rect.left && touch.clientX <= rect.right &&
      touch.clientY >= rect.top && touch.clientY <= rect.bottom
    ) return side
  }
  const pad = touch.target?.closest?.('.virtual-joystick')
  if (pad === leftPad.value) return 'left'
  if (props.dual && pad === rightPad.value) return 'right'
  return null
}

function startTouches(event) {
  if (!props.active) return
  let changed = false
  Array.from(event.changedTouches || []).forEach(touch => {
    if (touchSides.has(touch.identifier)) return
    const side = sideForTouch(touch)
    if (!side || sticks[side].pointerId !== null) return
    sticks[side].pointerId = touch.identifier
    sticks[side].inputType = 'touch'
    touchSides.set(touch.identifier, side)
    updateStick(side, touch, false)
    changed = true
  })
  if (!changed) return
  event.preventDefault()
  event.stopPropagation()
  emitValues()
  emit('interact')
}

function moveTouches(event) {
  if (!touchSides.size) return
  let changed = false
  Array.from(event.touches || []).forEach(touch => {
    const side = touchSides.get(touch.identifier)
    if (!side || sticks[side].inputType !== 'touch' || sticks[side].pointerId !== touch.identifier) return
    updateStick(side, touch, false)
    changed = true
  })
  if (!changed) return
  if (event.cancelable) event.preventDefault()
  emitValues()
}

function finishTouches(event) {
  if (!touchSides.size) return
  let changed = false
  Array.from(event.changedTouches || []).forEach(touch => {
    const side = touchSides.get(touch.identifier)
    if (!side) return
    touchSides.delete(touch.identifier)
    if (sticks[side].inputType === 'touch' && sticks[side].pointerId === touch.identifier) {
      reset(side, false)
      changed = true
    }
  })
  if (!changed) return
  if (event.cancelable) event.preventDefault()
  emitValues()
}

function cancelTouches(event) {
  if (!touchSides.size) return
  if (event.cancelable) event.preventDefault()
  resetAll()
}

function resetAll() {
  touchSides.clear()
  reset('left', false)
  reset('right', false)
  emitValues()
}

function thumbStyle(side) {
  const state = sticks[side]
  return { transform: `translate3d(${state.thumbX}px, ${state.thumbY}px, 0)` }
}

watch(() => [props.active, props.softwareRotated, props.dual], resetAll)

onMounted(() => {
  window.addEventListener('touchmove', moveTouches, { passive: false })
  window.addEventListener('touchend', finishTouches, { passive: false })
  window.addEventListener('touchcancel', cancelTouches, { passive: false })
})

onBeforeUnmount(() => {
  window.removeEventListener('touchmove', moveTouches)
  window.removeEventListener('touchend', finishTouches)
  window.removeEventListener('touchcancel', cancelTouches)
  resetAll()
})
</script>

<template>
  <div
    v-show="active"
    class="virtual-joysticks"
    :class="{ 'is-dual': dual }"
    :aria-label="dual ? '横屏飞行控制：左侧摇杆移动，右侧摇杆转动视角' : '竖屏飞行控制：左侧摇杆移动，手指滑动画面转动视角'"
    @touchstart="startTouches"
  >
    <div class="virtual-joysticks__side virtual-joysticks__side--left">
      <div
        ref="leftPad"
        class="virtual-joystick"
        role="slider"
        aria-label="移动摇杆"
        aria-valuetext="前后移动与左右横移"
        @pointerdown="start('left', $event)"
        @pointermove="move('left', $event)"
        @pointerup="finish('left', $event)"
        @pointercancel="finish('left', $event)"
        @lostpointercapture="finish('left', $event)"
      >
        <span class="virtual-joystick__ticks" aria-hidden="true"></span>
        <span class="virtual-joystick__thumb" :style="thumbStyle('left')" aria-hidden="true"></span>
      </div>
      <span class="virtual-joysticks__label">移动</span>
    </div>

    <div v-if="dual" class="virtual-joysticks__side virtual-joysticks__side--right">
      <div
        ref="rightPad"
        class="virtual-joystick"
        role="slider"
        aria-label="视角摇杆"
        aria-valuetext="控制镜头朝向"
        @pointerdown="start('right', $event)"
        @pointermove="move('right', $event)"
        @pointerup="finish('right', $event)"
        @pointercancel="finish('right', $event)"
        @lostpointercapture="finish('right', $event)"
      >
        <span class="virtual-joystick__ticks" aria-hidden="true"></span>
        <span class="virtual-joystick__thumb" :style="thumbStyle('right')" aria-hidden="true"></span>
      </div>
      <span class="virtual-joysticks__label">视角</span>
    </div>
  </div>
</template>
